import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import PageHeader from '../components/PageHeader';
import RecurringTemplateModal from '../components/RecurringTemplateModal';
import { getResources, getUserNotificationSettings, updateUserNotificationSettings, getRecurringEntries, applyRecurringEntry, deleteRecurringEntry, getRecurringEntriesProgress, RecurringProgressEntry } from '../services/api';
import { Resource, UserNotificationSettings, RecurringTimeEntry } from '../services/types';
import { isBetaUser, getCurrentResourceId } from '../utils/featureFlags';
import { useAuth } from '../contexts/AuthProvider';
import { useToast } from '../contexts/ToastProvider';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { getFrequencyDatesForMonth, getEnhancedDescription, getTemplateMonthlyHours } from '../utils/recurringEntryUtils';

interface UserPreferences {
  theme: 'dark' | 'light';
  notifications: boolean;
  defaultView: 'dashboard' | 'tickets' | 'calendar';
  refreshInterval: number;
}

const defaultPreferences: UserPreferences = {
  theme: 'dark',
  notifications: true,
  defaultView: 'dashboard',
  refreshInterval: 60,
};

export default function UserSettings() {
  const { user } = useAuth();
  const [preferences, setPreferences] = useState<UserPreferences>(() => {
    const saved = localStorage.getItem('autotask-hub-user-preferences');
    if (saved) {
      try {
        return { ...defaultPreferences, ...JSON.parse(saved) };
      } catch {
        return defaultPreferences;
      }
    }
    return defaultPreferences;
  });

  const [saved, setSaved] = useState(false);
  const [resources, setResources] = useState<Resource[]>([]);
  const [selectedResourceId, setSelectedResourceId] = useState<number | null>(getCurrentResourceId);
  const [betaEnabled, setBetaEnabled] = useState(() => {
    return localStorage.getItem('autotask-hub-beta-enabled') === 'true';
  });

  // Notification settings (from Cosmos DB)
  const [notifSettings, setNotifSettings] = useState<UserNotificationSettings | null>(null);
  const [notifLoading, setNotifLoading] = useState(false);
  const [notifSaving, setNotifSaving] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);
  const pushNotifications = usePushNotifications();
  const selectedResource = resources.find(r => r.autoTaskId === selectedResourceId);

  // Recurring entries state
  const [recurringEntries, setRecurringEntries] = useState<RecurringTimeEntry[]>([]);
  const [recurringLoading, setRecurringLoading] = useState(false);

  // Progress tracking
  const [progressData, setProgressData] = useState<Record<string, RecurringProgressEntry>>({});

  // Bulk apply state
  const [checkedTemplates, setCheckedTemplates] = useState<Set<string>>(new Set());
  const [bulkApplying, setBulkApplying] = useState(false);
  const isMountedRef = useRef(true);
  const { addToast, updateToast, removeToast } = useToast();

  // Modal state: null = closed, 'new' = create, RecurringTimeEntry = view existing
  const [modalTemplate, setModalTemplate] = useState<RecurringTimeEntry | null | 'new'>(null);

  // Track mount state for safe async updates
  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  // Load resources and auto-match logged-in user
  useEffect(() => {
    async function loadResources() {
      try {
        const data = await getResources();
        const filtered = data.filter(r =>
          r.isActive &&
          r.licenseType !== 3 &&
          r.autoTaskId !== 4 &&
          !(`${r.firstName} ${r.lastName}`.toLowerCase().includes('api'))
        );
        setResources(filtered);

        // Auto-match: if no resource selected (or mismatch), match by login email
        if (user?.email && user.email !== 'embed@hud.local') {
          const matched = filtered.find(
            r => r.email?.toLowerCase() === user.email.toLowerCase()
          );
          if (matched && matched.autoTaskId !== selectedResourceId) {
            setSelectedResourceId(matched.autoTaskId);
            localStorage.setItem('autotask-hub-resource-id', String(matched.autoTaskId));
            localStorage.setItem('autotask-hub-resource-email', matched.email || '');
          }
        }
      } catch (err) {
        console.error('Failed to load resources:', err);
      }
    }
    loadResources();
  }, [user?.email]);

  // Load notification settings when resource is selected
  useEffect(() => {
    if (!selectedResourceId) {
      setNotifSettings(null);
      return;
    }
    setNotifLoading(true);
    getUserNotificationSettings(selectedResourceId)
      .then(setNotifSettings)
      .catch(err => {
        console.error('Failed to load notification settings:', err);
        setNotifSettings(null);
      })
      .finally(() => setNotifLoading(false));
  }, [selectedResourceId]);

  // Save a notification setting change to the server
  const saveNotifSetting = async (updates: Partial<UserNotificationSettings>) => {
    if (!selectedResourceId) return;
    setNotifSaving(true);
    try {
      const updated = await updateUserNotificationSettings(selectedResourceId, updates);
      setNotifSettings(updated);
    } catch (err) {
      console.error('Failed to save notification settings:', err);
    } finally {
      setNotifSaving(false);
    }
  };

  // Load recurring entries when resource is selected
  const loadRecurringEntries = useCallback(async () => {
    if (!selectedResourceId) return;
    setRecurringLoading(true);
    try {
      const entries = await getRecurringEntries(selectedResourceId);
      setRecurringEntries(entries);
    } catch (err) {
      console.error('Failed to load recurring entries:', err);
    } finally {
      setRecurringLoading(false);
    }
  }, [selectedResourceId]);

  useEffect(() => { loadRecurringEntries(); }, [loadRecurringEntries]);

  // Load progress data when entries change
  const loadProgress = useCallback(async () => {
    if (!selectedResourceId || recurringEntries.length === 0) {
      setProgressData({});
      return;
    }
    const now = new Date();
    const ym = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    try {
      const data = await getRecurringEntriesProgress(selectedResourceId, ym);
      setProgressData(data);
    } catch {
      setProgressData({});
    }
  }, [selectedResourceId, recurringEntries]);

  useEffect(() => { loadProgress(); }, [loadProgress]);

  const handleDeleteRecurring = async (id: string) => {
    try {
      await deleteRecurringEntry(id);
      await loadRecurringEntries();
    } catch (err) {
      console.error('Failed to delete recurring entry:', err);
    }
  };

  // Handle push subscription toggle
  const handlePushToggle = async () => {
    if (!selectedResourceId) return;
    if (pushNotifications.isSubscribed) {
      await pushNotifications.unsubscribe(selectedResourceId);
    } else {
      await pushNotifications.subscribe(selectedResourceId);
    }
  };

  const handleSave = () => {
    localStorage.setItem('autotask-hub-user-preferences', JSON.stringify(preferences));

    // Save beta setting
    if (betaEnabled) {
      localStorage.setItem('autotask-hub-beta-enabled', 'true');
    } else {
      localStorage.removeItem('autotask-hub-beta-enabled');
    }

    setSaved(true);
    setTimeout(() => {
      setSaved(false);
      window.location.reload();
    }, 1000);
  };

  // Compute total monthly hours across all templates
  const currentMonthTotal = useMemo(() => {
    return recurringEntries.reduce((sum, t) => sum + getTemplateMonthlyHours(t), 0);
  }, [recurringEntries]);

  // Bulk apply: apply all checked templates for current month
  const handleBulkApply = async () => {
    if (!selectedResourceId || checkedTemplates.size === 0) return;
    const resource = resources.find(r => r.autoTaskId === selectedResourceId);
    const resourceName = resource ? `${resource.firstName} ${resource.lastName}` : '';
    const templates = recurringEntries.filter(t => checkedTemplates.has(t.id));
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();

    setBulkApplying(true);
    const toastId = addToast('progress', `Applying templates... 0 of ${templates.length}`, { current: 0, total: templates.length });

    let totalCreated = 0;
    let totalSkipped = 0;
    let totalFailed = 0;

    for (let i = 0; i < templates.length; i++) {
      const t = templates[i];
      const dates = getFrequencyDatesForMonth(t, year, month);
      if (dates.length === 0) {
        totalSkipped++;
        updateToast(toastId, {
          message: `${i + 1} of ${templates.length} \u2014 ${t.templateName} (no dates)`,
          progress: { current: i + 1, total: templates.length },
        });
        continue;
      }

      updateToast(toastId, {
        message: `${i + 1} of ${templates.length} \u2014 ${t.templateName}...`,
        progress: { current: i, total: templates.length },
      });

      try {
        const result = await applyRecurringEntry(t.id, dates, selectedResourceId, resourceName);
        totalCreated += result.created || 0;
        totalSkipped += result.skipped || 0;
        totalFailed += (result.errors || []).length;
      } catch {
        totalFailed++;
      }

      updateToast(toastId, {
        progress: { current: i + 1, total: templates.length },
      });
    }

    // Done - remove progress toast and show summary
    removeToast(toastId);

    if (totalFailed > 0) {
      addToast('warning', `Bulk apply done: ${totalCreated} created, ${totalSkipped} skipped, ${totalFailed} failed`);
    } else if (totalCreated === 0) {
      addToast('info', `All ${totalSkipped} entries already exist \u2014 nothing created`);
    } else {
      const parts = [`Created ${totalCreated} entries`];
      if (totalSkipped > 0) parts.push(`${totalSkipped} skipped`);
      addToast('success', parts.join(', '));
    }

    if (isMountedRef.current) {
      setBulkApplying(false);
      setCheckedTemplates(new Set());
      loadProgress(); // refresh progress badges
    }
  };

  // Progress badge helper
  const getProgressBadge = (t: RecurringTimeEntry) => {
    const now = new Date();
    const expected = getFrequencyDatesForMonth(t, now.getFullYear(), now.getMonth()).length;
    const expectedEntries = t.templateType === 'activity' && t.clients
      ? expected * t.clients.length : expected;
    if (expectedEntries === 0) return null;

    const progress = progressData[t.id];
    const applied = progress?.applied || 0;

    let bgClass: string;
    if (applied >= expectedEntries) {
      bgClass = 'bg-green-500/20 text-green-400 border border-green-500/30';
    } else if (applied > 0) {
      bgClass = 'bg-amber-500/20 text-amber-400 border border-amber-500/30';
    } else {
      bgClass = 'bg-slate-500/20 text-slate-400 border border-slate-500/30';
    }

    return (
      <span className={`text-[9px] px-1.5 py-0.5 rounded font-medium ${bgClass}`}>
        {applied}/{expectedEntries}
      </span>
    );
  };

  const handleModalSaved = () => {
    loadRecurringEntries();
    // Progress will reload via the useEffect dependency on recurringEntries
  };

  return (
    <div className="space-y-4">
      <PageHeader
        userEmail={user?.email}
        middleContent={
          <div className="text-center">
            <span className="text-sm font-medium text-white">User Settings</span>
            <span className="text-xs text-slate-500 ml-2">Personal preferences</span>
          </div>
        }
      />

      {/* Identity */}
      <div className="bg-slate-800 rounded-lg border border-slate-700 p-4">
        <h3 className="text-sm font-semibold text-white mb-3">Your Identity</h3>
        {selectedResource ? (
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white font-semibold text-sm">
              {selectedResource.firstName?.[0]}{selectedResource.lastName?.[0]}
            </div>
            <div>
              <div className="text-sm font-medium text-white">{selectedResource.firstName} {selectedResource.lastName}</div>
              <div className="text-xs text-slate-500">{selectedResource.email || user?.email}</div>
            </div>
          </div>
        ) : (
          <p className="text-sm text-slate-400">
            Could not match your login ({user?.email}) to an AutoTask resource. Contact an admin.
          </p>
        )}
      </div>

      {/* Preferences */}
      <div className="bg-slate-800 rounded-lg border border-slate-700 p-4 space-y-4">
        <h3 className="text-sm font-semibold text-white mb-3">Preferences</h3>

        {/* Theme */}
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-2">
            Theme
          </label>
          <select
            value={preferences.theme}
            onChange={(e) => setPreferences({ ...preferences, theme: e.target.value as 'dark' | 'light' })}
            className="bg-slate-700 border border-slate-600 rounded-md px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="dark">Dark</option>
            <option value="light">Light</option>
          </select>
        </div>

        {/* Refresh Interval */}
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-2">
            Auto-refresh Interval
          </label>
          <select
            value={preferences.refreshInterval}
            onChange={(e) => setPreferences({ ...preferences, refreshInterval: Number(e.target.value) })}
            className="bg-slate-700 border border-slate-600 rounded-md px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value={30}>30 seconds</option>
            <option value={60}>1 minute</option>
            <option value={120}>2 minutes</option>
            <option value={300}>5 minutes</option>
            <option value={0}>Disabled</option>
          </select>
        </div>

        {/* Notifications */}
        <div className="flex items-center space-x-3">
          <input
            type="checkbox"
            id="notifications"
            checked={preferences.notifications}
            onChange={(e) => setPreferences({ ...preferences, notifications: e.target.checked })}
            className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-blue-500 focus:ring-blue-500"
          />
          <label htmlFor="notifications" className="text-sm text-slate-300">
            Enable browser notifications
          </label>
        </div>
      </div>

      {/* Push Notifications & Location */}
      {selectedResourceId && (
        <div className="bg-slate-800 rounded-lg border border-slate-700 p-4 space-y-4">
          <h3 className="text-sm font-semibold text-white mb-3">Notifications & Location</h3>

          {notifLoading ? (
            <div className="flex items-center gap-2 text-sm text-slate-400">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500" />
              Loading settings...
            </div>
          ) : notifSettings ? (
            <>
              {/* Push Subscription */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-slate-300">Push Notifications</label>
                    <p className="text-xs text-slate-500">
                      {pushNotifications.isSupported
                        ? pushNotifications.isSubscribed
                          ? 'Subscribed - you will receive push notifications'
                          : 'Not subscribed'
                        : 'Not supported in this browser'}
                    </p>
                  </div>
                  <button
                    onClick={handlePushToggle}
                    disabled={!pushNotifications.isSupported || pushNotifications.isLoading}
                    className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                      pushNotifications.isSubscribed
                        ? 'bg-red-600/20 text-red-400 border border-red-500/30 hover:bg-red-600/30'
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    } disabled:opacity-50`}
                  >
                    {pushNotifications.isLoading
                      ? 'Processing...'
                      : pushNotifications.isSubscribed
                      ? 'Unsubscribe'
                      : 'Subscribe'}
                  </button>
                </div>
                {pushNotifications.error && (
                  <p className="text-xs text-red-400">{pushNotifications.error}</p>
                )}
              </div>

              {/* Notification Type Toggles */}
              <div className="border-t border-slate-700 pt-3 space-y-2">
                <p className="text-xs text-slate-500 mb-2">Choose which notifications you receive:</p>
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="notifyNewTickets"
                    checked={notifSettings.notifyNewTickets}
                    onChange={(e) => saveNotifSetting({ notifyNewTickets: e.target.checked })}
                    disabled={notifSaving}
                    className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-blue-500 focus:ring-blue-500"
                  />
                  <label htmlFor="notifyNewTickets" className="text-sm text-slate-300">
                    New tickets in queue
                  </label>
                </div>
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="notifyAssignedTickets"
                    checked={notifSettings.notifyAssignedTickets}
                    onChange={(e) => saveNotifSetting({ notifyAssignedTickets: e.target.checked })}
                    disabled={notifSaving}
                    className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-blue-500 focus:ring-blue-500"
                  />
                  <label htmlFor="notifyAssignedTickets" className="text-sm text-slate-300">
                    Tickets assigned to me
                  </label>
                </div>
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="notifyCustomerReplies"
                    checked={notifSettings.notifyCustomerReplies}
                    onChange={(e) => saveNotifSetting({ notifyCustomerReplies: e.target.checked })}
                    disabled={notifSaving}
                    className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-blue-500 focus:ring-blue-500"
                  />
                  <label htmlFor="notifyCustomerReplies" className="text-sm text-slate-300">
                    Customer replies on my tickets
                  </label>
                </div>
              </div>

              {/* Email Notification Preferences */}
              <div className="border-t border-slate-700 pt-3 space-y-2">
                <p className="text-xs text-slate-500 mb-2">Email notifications for ticket events:</p>
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="emailTicketAssigned"
                    checked={notifSettings.emailTicketAssigned !== false}
                    onChange={(e) => saveNotifSetting({ emailTicketAssigned: e.target.checked })}
                    disabled={notifSaving}
                    className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-blue-500 focus:ring-blue-500"
                  />
                  <label htmlFor="emailTicketAssigned" className="text-sm text-slate-300">
                    Email when ticket assigned to me
                  </label>
                </div>
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="emailStatusChanged"
                    checked={notifSettings.emailStatusChanged !== false}
                    onChange={(e) => saveNotifSetting({ emailStatusChanged: e.target.checked })}
                    disabled={notifSaving}
                    className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-blue-500 focus:ring-blue-500"
                  />
                  <label htmlFor="emailStatusChanged" className="text-sm text-slate-300">
                    Email on ticket status changes
                  </label>
                </div>
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="emailNoteAdded"
                    checked={notifSettings.emailNoteAdded !== false}
                    onChange={(e) => saveNotifSetting({ emailNoteAdded: e.target.checked })}
                    disabled={notifSaving}
                    className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-blue-500 focus:ring-blue-500"
                  />
                  <label htmlFor="emailNoteAdded" className="text-sm text-slate-300">
                    Email when note added on my ticket
                  </label>
                </div>
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="emailTimeAdded"
                    checked={notifSettings.emailTimeAdded === true}
                    onChange={(e) => saveNotifSetting({ emailTimeAdded: e.target.checked })}
                    disabled={notifSaving}
                    className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-blue-500 focus:ring-blue-500"
                  />
                  <label htmlFor="emailTimeAdded" className="text-sm text-slate-300">
                    Email when time logged on my ticket
                  </label>
                </div>
              </div>

              {/* Work Hours */}
              <div className="border-t border-slate-700 pt-3">
                <p className="text-xs text-slate-500 mb-2">
                  Notifications and location tracking are only active during work hours:
                </p>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-1">
                    <label className="text-xs text-slate-400">From</label>
                    <select
                      value={notifSettings.workHoursStart}
                      onChange={(e) => saveNotifSetting({ workHoursStart: parseInt(e.target.value) })}
                      disabled={notifSaving}
                      className="bg-slate-700 border border-slate-600 rounded px-2 py-1 text-white text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      {Array.from({ length: 24 }, (_, i) => (
                        <option key={i} value={i}>
                          {i === 0 ? '12 AM' : i < 12 ? `${i} AM` : i === 12 ? '12 PM' : `${i - 12} PM`}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-center gap-1">
                    <label className="text-xs text-slate-400">To</label>
                    <select
                      value={notifSettings.workHoursEnd}
                      onChange={(e) => saveNotifSetting({ workHoursEnd: parseInt(e.target.value) })}
                      disabled={notifSaving}
                      className="bg-slate-700 border border-slate-600 rounded px-2 py-1 text-white text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      {Array.from({ length: 24 }, (_, i) => (
                        <option key={i} value={i}>
                          {i === 0 ? '12 AM' : i < 12 ? `${i} AM` : i === 12 ? '12 PM' : `${i - 12} PM`}
                        </option>
                      ))}
                    </select>
                  </div>
                  <span className="text-xs text-slate-500">Mon-Fri only</span>
                </div>
              </div>

              {/* Location Sharing Toggle */}
              <div className="border-t border-slate-700 pt-3">
                <div className="flex items-center justify-between">
                  <div className="flex-1 mr-3">
                    <label className="text-sm font-medium text-slate-300">Location Sharing</label>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {notifSettings.locationConsent
                        ? 'Active — your general location is shared during work hours so dispatchers can route the closest tech.'
                        : 'Off — enable to share your general location during work hours (Mon–Fri). Browser will prompt for permission. Low accuracy, battery-friendly.'}
                    </p>
                    {locationError && (
                      <p className="text-xs text-red-400 mt-1">{locationError}</p>
                    )}
                  </div>
                  <button
                    onClick={async () => {
                      if (!selectedResourceId) return;
                      setLocationError(null);
                      if (notifSettings.locationConsent) {
                        // Turning off — just save
                        await saveNotifSetting({ locationConsent: false });
                        return;
                      }
                      // Turning on — request geolocation permission first
                      if (!('geolocation' in navigator)) {
                        setLocationError('Geolocation not supported by this browser.');
                        return;
                      }
                      try {
                        await new Promise<GeolocationPosition>((resolve, reject) => {
                          navigator.geolocation.getCurrentPosition(resolve, reject, {
                            enableHighAccuracy: false,
                            timeout: 10000,
                          });
                        });
                        await saveNotifSetting({ locationConsent: true });
                      } catch (err: unknown) {
                        const e = err as { code?: number; message?: string };
                        if (e?.code === 1) {
                          setLocationError('Permission denied. Re-enable geolocation in your browser site settings, then try again.');
                        } else if (e?.code === 2) {
                          setLocationError('Position unavailable. Check that location services are on and try again.');
                        } else if (e?.code === 3) {
                          setLocationError('Location request timed out. Try again.');
                        } else {
                          setLocationError(e?.message || 'Failed to get location.');
                        }
                      }
                    }}
                    disabled={notifSaving || !selectedResourceId}
                    className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                      notifSettings.locationConsent
                        ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                        : 'bg-slate-600 text-slate-200 hover:bg-slate-500'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {notifSaving ? 'Saving...' : notifSettings.locationConsent ? 'Disable' : 'Enable'}
                  </button>
                </div>
              </div>

              {notifSaving && (
                <div className="text-xs text-blue-400 flex items-center gap-1">
                  <div className="animate-spin rounded-full h-3 w-3 border-b border-blue-400" />
                  Saving...
                </div>
              )}
            </>
          ) : (
            <p className="text-sm text-slate-400">Select your identity above to configure notifications.</p>
          )}
        </div>
      )}

      {/* Recurring Time Entries */}
      {selectedResourceId && (
        <div className="bg-slate-800 rounded-lg border border-slate-700 p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold text-white">Recurring Time Entries</h3>
              {recurringEntries.length > 0 && currentMonthTotal > 0 && (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-500/20 text-blue-400 border border-blue-500/30 font-medium">
                  {currentMonthTotal % 1 === 0 ? currentMonthTotal : currentMonthTotal.toFixed(1)}h/mo
                </span>
              )}
            </div>
            <button
              onClick={() => setModalTemplate('new')}
              className="px-3 py-1 text-xs font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors"
            >
              + New Template
            </button>
          </div>
          <p className="text-xs text-slate-400 mb-3">
            Templates for entries you log regularly. Click a row to view details and apply, or use checkboxes for bulk apply.
          </p>

          {recurringLoading ? (
            <div className="flex items-center gap-2 text-sm text-slate-400">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500" />
              Loading templates...
            </div>
          ) : recurringEntries.length === 0 ? (
            <p className="text-sm text-slate-500">No recurring entry templates yet.</p>
          ) : (
            <div className="space-y-2">
              {/* Bulk action bar */}
              <div className="flex items-center gap-3 text-xs">
                <button
                  onClick={() => setCheckedTemplates(new Set(recurringEntries.map(t => t.id)))}
                  className="text-blue-400 hover:text-blue-300 transition-colors"
                >
                  Select All
                </button>
                <button
                  onClick={() => setCheckedTemplates(new Set())}
                  className="text-slate-400 hover:text-slate-300 transition-colors"
                >
                  Select None
                </button>
                {checkedTemplates.size > 0 && (
                  <button
                    onClick={handleBulkApply}
                    disabled={bulkApplying}
                    className="ml-auto px-3 py-1 text-[11px] font-medium rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 transition-colors"
                  >
                    {bulkApplying ? 'Applying...' : `Apply Selected (${checkedTemplates.size})`}
                  </button>
                )}
              </div>

              {recurringEntries.map(t => (
                <div
                  key={t.id}
                  className="flex items-center gap-2 bg-slate-700/50 rounded-lg px-3 py-2 border border-slate-600/50 cursor-pointer hover:border-slate-500/70 transition-colors"
                  onClick={() => setModalTemplate(t)}
                >
                  {/* Checkbox — stop propagation so clicking it doesn't open modal */}
                  <input
                    type="checkbox"
                    checked={checkedTemplates.has(t.id)}
                    onChange={e => {
                      setCheckedTemplates(prev => {
                        const next = new Set(prev);
                        if (e.target.checked) next.add(t.id);
                        else next.delete(t.id);
                        return next;
                      });
                    }}
                    onClick={e => e.stopPropagation()}
                    className="w-3.5 h-3.5 rounded border-slate-500 bg-slate-600 text-blue-500 focus:ring-blue-500 shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-white truncate">{t.templateName}</span>
                      <span className={`text-[9px] px-1.5 py-0.5 rounded font-medium ${
                        (t.templateType || 'client') === 'client'
                          ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                          : 'bg-purple-500/20 text-purple-400 border border-purple-500/30'
                      }`}>
                        {(t.templateType || 'client') === 'client' ? 'CLIENT' : 'ACTIVITY'}
                      </span>
                      {t.frequency === 'biweekly' && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded font-medium bg-amber-500/20 text-amber-400 border border-amber-500/30 flex items-center gap-0.5">
                          <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 5l7 7-7 7" /></svg>
                          2WK
                        </span>
                      )}
                      {getProgressBadge(t)}
                    </div>
                    <div className="text-xs text-slate-400 mt-0.5">
                      {getEnhancedDescription(t)}
                    </div>
                  </div>
                  {/* Delete button — stop propagation */}
                  <button
                    onClick={e => { e.stopPropagation(); handleDeleteRecurring(t.id); }}
                    className="p-1 text-xs text-slate-400 hover:text-red-400 transition-colors shrink-0"
                    title="Delete"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Beta Features */}
      <div className="bg-slate-800 rounded-lg border border-yellow-600/30 p-4">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-lg">🧪</span>
          <h3 className="text-sm font-semibold text-white">Beta Features</h3>
          <span className="text-[9px] bg-yellow-500/20 text-yellow-400 px-1.5 py-0.5 rounded">EXPERIMENTAL</span>
        </div>
        <p className="text-sm text-slate-400 mb-4">
          Enable early access to new features. These may be incomplete or have bugs.
        </p>

        <div className="flex items-center space-x-3">
          <input
            type="checkbox"
            id="betaFeatures"
            checked={betaEnabled}
            onChange={(e) => setBetaEnabled(e.target.checked)}
            className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-yellow-500 focus:ring-yellow-500"
          />
          <label htmlFor="betaFeatures" className="text-sm text-slate-300">
            Enable beta features (Calendar, Time Tracking, etc.)
          </label>
        </div>

        {betaEnabled && (
          <div className="mt-3 p-3 bg-yellow-900/20 rounded-lg border border-yellow-600/20">
            <div className="text-xs text-yellow-400 font-medium mb-1">Beta features enabled:</div>
            <ul className="text-xs text-yellow-200/70 space-y-1">
              <li>• Reports v2 - New reporting system</li>
            </ul>
          </div>
        )}

        {isBetaUser() && !betaEnabled && (
          <div className="mt-3 text-xs text-green-400">
            Note: You're already a beta tester by resource ID.
          </div>
        )}
      </div>

      {/* Save Button */}
      <div className="bg-slate-800 rounded-lg border border-slate-700 p-4">
        <button
          onClick={handleSave}
          className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-6 py-2 rounded-md text-sm transition-colors"
        >
          {saved ? 'Saved! Reloading...' : 'Save Settings'}
        </button>
        <p className="text-xs text-slate-500 mt-2">
          Page will reload to apply changes.
        </p>
      </div>

      {/* Recurring Template Modal */}
      {selectedResourceId && (
        <RecurringTemplateModal
          isOpen={modalTemplate !== null}
          template={modalTemplate === 'new' ? null : modalTemplate}
          onClose={() => setModalTemplate(null)}
          onSaved={handleModalSaved}
          resourceId={selectedResourceId}
          resources={resources}
          progressData={modalTemplate && modalTemplate !== 'new' ? progressData[modalTemplate.id] : undefined}
        />
      )}
    </div>
  );
}
